import { basicAuth, headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const personalTokenVariant = productVariant({
  identifier: "honeybadger",
  authLabel: "Personal authentication token",
  protocol: "http",
  defaultUrlPattern: () => "https://app\\.honeybadger\\.io/.*",
  auth: (app) =>
    basicAuth({
      username: app.secret.authToken,
      password: "",
    }),
  steps: [
    {
      title: "Get your authentication token",
      items: [
        {
          kind: "markdown",
          content:
            "Visit your [Honeybadger profile page](https://app.honeybadger.io/users/edit) and copy your personal authentication token.",
        },
      ],
    },
    {
      title: "Copy and paste the authentication token into the field below",
      items: [
        {
          kind: "input",
          label: "Personal authentication token",
          required: true,
          target: { kind: "secrets", field: "authToken" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://app.honeybadger.io/v2/projects",
  },
});

const projectApiKeyVariant = productVariant({
  identifier: "honeybadgerProjectApiKey",
  authLabel: "Project API key",
  protocol: "http",
  defaultUrlPattern: () =>
    "https://api\\.honeybadger\\.io/v1/(notices|events|deploys)(\\?.*)?$",
  auth: (app) => headerAuth([{ name: "X-API-Key", value: app.secret.apiKey }]),
  steps: [
    {
      title: "Get your project API key",
      items: [
        {
          kind: "markdown",
          content:
            "In [Honeybadger](https://app.honeybadger.io/), open your project’s **Settings › API Key** and copy the API key. Use this key to report errors, events, and JSON deployments. Reading or managing data through the Data API requires a personal authentication token. Testing this connector sends a test event to your project.",
        },
      ],
    },
    {
      title: "Paste your project API key",
      items: [
        {
          kind: "input",
          label: "Project API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://api.honeybadger.io/v1/events",
      method: "POST",
      headers: {
        "Content-Type": "application/x-ndjson",
        Accept: "application/json",
        "User-Agent": "3B-Connector",
      },
      body: JSON.stringify({ event: "3b.connector.test" }) + "\n",
      expectedStatus: 201,
    }),
  },
  usageHint: () =>
    "Uses X-API-Key for POST requests to https://api.honeybadger.io/v1/notices, /v1/events, and /v1/deploys. Send deployments as JSON. This project key cannot access the Data API; use a personal authentication token for that.",
});

export const honeybadger = defineProduct({
  name: "Honeybadger",
  tagLine: "Error, uptime, and check-in monitoring",
  logo,
  tags: ["observability"],
  variants: [personalTokenVariant, projectApiKeyVariant],
});
